import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { OfficeBuildingIcon, UserCircleIcon } from '@heroicons/react/outline';
import { UsersIcon } from '@heroicons/react/solid';

import { getAssessmentsDataExport } from '../../../api/rest/assessments';
import {
  ProviderUserRole,
  useUpdateProviderMutation,
} from '../../../generated/graphql';

import { useAuth } from '../../../contexts/AuthContext';
import { defaultTransitionProps } from '../../lib/animation';
import { customToast } from '../../components/ToastAlert/customToast';

import ProfilePhoto from './ProfilePhoto';
import TimezoneSelector from '../../components/TimezoneSelector';
import ChangePasswordModal from './ChangePasswordModal';
import ResetOnboardingModal from './ResetOnboardingModal';
import SettingsItem, { SettingsRow } from './SettingsItem';
import { isProviderUserOwnerOrAdminAuthorized } from '../../../lib/auth';
import RenameProviderModal from './Modals/RenameProviderModal';
import Loading from '../Loading';
import RenameProviderUserModal from './Modals/RenameProviderUserModal';

const AccountSection: FC = () => {
  const navigate = useNavigate();
  const {
    authedProviderUser,
    isAuthedProviderUserLoading,
    refreshAuthedProviderUser,
  } = useAuth();
  const [updateProvider] = useUpdateProviderMutation();

  const isOwner = authedProviderUser?.role === ProviderUserRole.Owner;
  const providerTimezone = (
    authedProviderUser?.provider as { timezone?: string }
  )?.timezone;

  const updateTimezone = async (timezone: string) => {
    try {
      await updateProvider({
        variables: { input: { timezone } },
      });
      customToast.success('Timezone updated!');
      await refreshAuthedProviderUser();
    } catch {
      customToast.error('Failed to update timezone. Please try again later.');
    }
  };

  // Disable in V2 until the export functionality is expanded to make sense for V2 users
  // The existing version was built in the context of a V1 Clinic, specifically 1 clinics
  // particular needs, and as is I don't think it will be useful or make sense for V2 users.
  const canExportAssessmentsData = false;

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isResetOnboardingModalOpen, setIsResetOnboardingModalOpen] =
    useState(false);
  const [isRenameProviderModalOpen, setIsRenameProviderModalOpen] =
    useState(false);
  const [isRenameProviderUserModalOpen, setIsRenameProviderUserModalOpen] =
    useState(false);

  const downloadAssessmentsData = () => {
    getAssessmentsDataExport();
  };

  const organizationItems: SettingsRow[] = [
    {
      label: 'Name',
      data: authedProviderUser?.provider.name ?? '',
      ...(isProviderUserOwnerOrAdminAuthorized(authedProviderUser) && {
        onClickEdit: () => setIsRenameProviderModalOpen(true),
      }),
    },
    ...(isOwner
      ? [
          {
            label: 'Timezone',
            innerComponent: (
              <div className="mt-2 max-w-[300px]">
                <TimezoneSelector
                  value={providerTimezone ?? ''}
                  onChange={updateTimezone}
                  placeholder={providerTimezone ? undefined : 'Select timezone'}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(isProviderUserOwnerOrAdminAuthorized(authedProviderUser)
      ? [
          {
            label: '',
            data: 'Customize your Homecoming experience',
            onClick: () => navigate('/settings/appearance'),
          },
        ]
      : []),
  ];

  const providerUserItems: SettingsRow[] = [
    {
      label: 'Name',
      data: authedProviderUser?.name ?? '',
      onClickEdit: () => setIsRenameProviderUserModalOpen(true),
    },
    { label: 'Email', data: authedProviderUser?.email ?? '' },
    {
      label: 'Profile photo',
      innerComponent: <ProfilePhoto />,
    },
    ...(canExportAssessmentsData
      ? [
          {
            label: 'Assessments',
            data: 'Export Assessments Data',
            onClick: () => downloadAssessmentsData(),
          },
        ]
      : []),
    ...(authedProviderUser?.provider.isTestProvider
      ? [
          {
            label: 'Testing',
            data: 'Reset Onboarding',
            onClick: () => setIsResetOnboardingModalOpen(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="mt-4 flex w-[750px] flex-col">
        <Transition
          show={Boolean(authedProviderUser)}
          {...defaultTransitionProps}
        >
          <div className="mb-8">
            <div className="mb-3 flex items-center gap-2 text-category font-medium">
              <OfficeBuildingIcon className="h-5 w-5 text-neutral-125" />
              Your organization
            </div>
            {organizationItems.map((settingsItem, index) => (
              <SettingsItem
                key={`settingsItem_${settingsItem.label || index}`}
                settingsRow={settingsItem}
              />
            ))}
          </div>
          <div>
            <div className="mb-3 flex items-center gap-2 text-category font-medium">
              <UserCircleIcon className="h-5 w-5 text-neutral-125" />
              Your account
            </div>
            {(authedProviderUser as { isExternalCollaborator?: boolean })
              ?.isExternalCollaborator && (
              <div className="mb-3 flex rounded-md bg-orange-25/30 p-4 text-orange-125">
                <UsersIcon
                  className="h-5 min-h-[20px] w-5 min-w-[20px] text-orange-100"
                  aria-hidden="true"
                />
                <span className="ml-3 font-sans text-caption font-medium">
                  Collaborating with {authedProviderUser?.provider.name ?? ''}
                </span>
              </div>
            )}
            {providerUserItems.map((settingsItem) => (
              <SettingsItem
                key={`settingsItem_${settingsItem.label}`}
                settingsRow={settingsItem}
              />
            ))}
          </div>
        </Transition>

        {isAuthedProviderUserLoading && !authedProviderUser && <Loading />}
      </div>

      <ChangePasswordModal
        isModalOpen={isChangePasswordModalOpen}
        setClosed={() => setIsChangePasswordModalOpen(false)}
      />
      <ResetOnboardingModal
        isModalOpen={isResetOnboardingModalOpen}
        setClosed={() => setIsResetOnboardingModalOpen(false)}
        providerUserEmail={authedProviderUser?.email}
      />
      <RenameProviderModal
        isModalOpen={isRenameProviderModalOpen}
        setClosed={() => setIsRenameProviderModalOpen(false)}
        providerName={authedProviderUser?.provider.name}
        onComplete={async () => {
          await refreshAuthedProviderUser();
        }}
      />
      <RenameProviderUserModal
        isModalOpen={isRenameProviderUserModalOpen}
        setClosed={() => setIsRenameProviderUserModalOpen(false)}
        providerUserName={authedProviderUser?.name}
        onComplete={async () => {
          await refreshAuthedProviderUser();
        }}
      />
    </>
  );
};

export default AccountSection;
